import { css } from '@styled-system/css';
import { Lock } from 'lucide-react';
import { type FC, type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';

import { useVestiaireLockedNotice } from '@/features/subscription/hooks/useVestiaireLockedNotice';
import { PlatformLogo } from '@/shared/components/analysis/PlatformLogo';
import { Button, type ButtonSize, type ButtonVariant } from '@/shared/components/ui/Button';
import { ChipToggle } from '@/shared/components/ui/ChipToggle';
import { Tooltip } from '@/shared/components/ui/Tooltip';

// ============================================================================
// Recherche Vestiaire Collective verrouillée par le plan (2026-10-01) : la puce
// de plateforme grisée et les actions de création depuis une analyse VC
// (relance de niche, « articles similaires »). Jamais l'attribut natif
// `disabled` : un bouton désactivé ne reçoit pas le survol, l'infobulle ne
// s'afficherait pas (`aria-disabled` à la place, rendu grisé porté par
// `ChipToggle`/`Button`). Survol (bureau) : infobulle `lockedHint`. Clic ou
// tap : notification + lien vers l'abonnement (`useVestiaireLockedNotice`),
// sans navigation directe. Lecteurs d'écran : `aria-describedby` vers un texte
// masqué TOUJOURS présent (l'infobulle n'existe dans le DOM qu'ouverte).
// ============================================================================

/** Enveloppe de l'infobulle : un `span` porte le déclencheur, comme pour les boutons de `NichesTable`. */
const triggerWrap = css({ display: 'inline-flex' });
const visuallyHidden = css({ srOnly: true });

/** Infobulle + description accessible communes aux commandes verrouillées. */
const LockedShell: FC<{ children: (describedById: string) => ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const describedById = useId();
  const hint = t('vestiaireSearch.lockedHint');
  return (
    <Tooltip label={hint} multiline>
      <span className={triggerWrap}>
        {children(describedById)}
        <span id={describedById} className={visuallyHidden}>
          {hint}
        </span>
      </span>
    </Tooltip>
  );
};

/** Puce « Vestiaire Collective » verrouillée : grisée, cadenas, infobulle, clic → notification. */
export const VestiaireLockedChip: FC = () => {
  const { t } = useTranslation();
  const showLockedNotice = useVestiaireLockedNotice();
  return (
    <LockedShell>
      {(describedById) => (
        <ChipToggle
          selected={false}
          onChange={showLockedNotice}
          icon={<PlatformLogo platform="vestiaire" />}
          trailingIcon={<Lock size={13} strokeWidth={1.75} aria-hidden />}
          ariaDisabled
          ariaDescribedBy={describedById}
        >
          {t('vestiaireSearch.tabVestiaire')}
        </ChipToggle>
      )}
    </LockedShell>
  );
};

interface VestiaireLockedButtonProps {
  variant: ButtonVariant;
  size: ButtonSize;
  /** Taille du cadenas (celle de l'icône qu'il remplace). */
  iconSize: number;
  iconStrokeWidth?: number;
  /** Libellé visible ; absent pour un bouton icône seule (alors `ariaLabel`). */
  children?: ReactNode;
  ariaLabel?: string;
}

/**
 * Bouton d'action de création verrouillé (relance de niche, « articles similaires ») : grisé,
 * cadenas, infobulle ; le clic n'appelle jamais l'action, il affiche la notification d'abonnement.
 */
export const VestiaireLockedButton: FC<VestiaireLockedButtonProps> = ({
  variant,
  size,
  iconSize,
  iconStrokeWidth = 1.75,
  children,
  ariaLabel,
}) => {
  const showLockedNotice = useVestiaireLockedNotice();
  return (
    <LockedShell>
      {(describedById) => (
        <Button
          variant={variant}
          size={size}
          icon={<Lock size={iconSize} strokeWidth={iconStrokeWidth} aria-hidden />}
          // `Button` arrête déjà la propagation (ligne de tableau, carte cliquable)
          onClick={showLockedNotice}
          aria-label={ariaLabel}
          aria-disabled
          aria-describedby={describedById}
        >
          {children}
        </Button>
      )}
    </LockedShell>
  );
};
